import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  MessageSquare,
  Database,
  Sliders,
  GitBranch,
  FileText,
  Save,
  Loader2,
  ChevronRight,
  Lock,
  CheckCircle,
  AlertCircle,
  X,
  Download,
  Upload,
  Plus,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useChat } from "../contexts/ChatContext";
import { useAuth } from "../contexts/AuthContext";
import CustomSelect from "./CustomSelect";
import { editableConfigTypesFor } from "../utils/permissions";
import { prepareConfigImport } from "../utils/configImport";
import { inapplicableScheduleKeys } from "../utils/scheduleMode";

/* ─── Config type metadata ─── */
const CONFIG_META = {
  vector_store: {
    icon: Database,
    color: "var(--color-mint)",
    bgColor: "var(--color-mint-muted)",
  },
  user_editable: {
    icon: Sliders,
    color: "var(--color-mint)",
    bgColor: "var(--color-mint-muted)",
  },
  workflow: {
    icon: GitBranch,
    color: "var(--color-mint)",
    bgColor: "var(--color-mint-muted)",
  },
  prompts: {
    icon: FileText,
    color: "var(--color-mint)",
    bgColor: "var(--color-mint-muted)",
  },
};

/* The config object a type with NO stored row renders from. Module-level so the
   identity is stable across renders, and frozen so nothing can start treating it as a
   place to collect values: the tab has no fields to collect. */
const EMPTY_CONFIG = Object.freeze({});

/* ─── Helpers ─── */
const formatLabel = (key) => {
  return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

/* Why a group of toggles is hidden -> the sentence that explains it. The API names the
   CAUSE, not the field, so one message covers every field that shares it. */
const HIDDEN_REASON_KEYS = {
  platform_type: "platformToolsNeedPlatform",
  voice_channel: "voiceToolsNeedChannel",
};

/* The caption for a schedule key the mode makes inapplicable, keyed by the key
   that is hidden. Same purpose as HIDDEN_REASON_KEYS above, and the same reason
   renderBoolMapBox shows its caption in read mode too: a field that is simply
   absent reads as a field that does not exist, and the reader cannot click Edit
   to find out otherwise.

   RPAA-265 exists to draw the line between "not set" and "not applicable", and
   hiding without saying so redraws it in a new place — a field that vanished.
   Carrying the payload half of renderBoolMapBox's precedent (the key stays in
   formValues) without the caption half was the gap an independent review found
   on 09-Set-2026 (M1). */
const INAPPLICABLE_SCHEDULE_REASON_KEYS = {
  product_ingestion_interval_minutes: "intervalHiddenByDailyTimesMode",
  product_ingestion_daily_times: "dailyTimesHiddenByIntervalMode",
};

/* ─── A map of name -> boolean (enabled_tools, voice_enabled_tools) ─── */
/* Requires at least one entry: an empty object ({} — fit_proximity,
   text_normalizations) would vacuously pass `every` and lose its JSON editor,
   leaving no way to add the first entry. */
const isBoolMap = (value) =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0 &&
  Object.values(value).every((v) => typeof v === "boolean");

/* ─── Auto-detect value type for smart rendering ─── */
const detectValueType = (key, value) => {
  if (typeof value === "boolean" || key === "internal_chat") return "boolean";
  if (isBoolMap(value)) return "boolMap";
  if (typeof value === "object" && value !== null) return "json";
  if (typeof value === "number") return "number";
  const str = String(value);
  if (str.length > 300) return "largeText";
  if (str.length > 100) return "mediumText";
  return "text";
};

/* ─── Toggle component ─── */
const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
    style={{
      backgroundColor: checked ? "var(--color-mint)" : "#d1d5db",
      focusRingColor: "var(--color-mint)",
    }}
  >
    <span
      className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ease-in-out"
      style={{
        transform: checked ? "translateX(20px)" : "translateX(2px)",
        marginTop: "2px",
      }}
    />
  </button>
);

/* ─── JSON Validator ─── */
const validateJson = (str) => {
  try {
    JSON.parse(str);
    return { valid: true };
  } catch (e) {
    return { valid: false, error: e.message };
  }
};

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const ConfigurationsPage = ({ configs, isLoading, onUpdate, visibility = {}, missingTypes = [] }) => {
  const { translate } = useLanguage();
  const { chats, selectedChatId, setSelectedChatId, loading: chatsLoading, chatsLoaded, loadChats, getSelectedChat } = useChat();
  const { isPlatformOnlySuperAdmin, hasAllAccess, effectiveRoles } = useAuth();

  const [activeTab, setActiveTab] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [jsonErrors, setJsonErrors] = useState({});
  const [importError, setImportError] = useState(null);
  const fileInputRef = useRef(null);

  const selectedChat = getSelectedChat();
  const activeChatsCount = chats ? chats.filter((chat) => chat.is_active).length : 0;
  /* The types with a stored row first, then the ones with none. `missing_types` comes
     from the response envelope, never from `configs`, which is echoed back on save.

     A type that is missing has no key in `configs`, so before this it had no tab, and
     the tab is the only place an operator can create the row from. That left a new chat,
     or a tenant that never had curated cards, with no path at all in the UI, while the
     backend now REQUIRES that PUT before the chat serves traffic (RPAA-225). */
  const missingTypeSet = useMemo(() => new Set(missingTypes), [missingTypes]);
  const configTypes = useMemo(
    () => [...(configs ? Object.keys(configs) : []), ...missingTypes],
    [configs, missingTypes]
  );

  // Set default active tab, or reset if current tab no longer exists
  useEffect(() => {
    if (configTypes.length > 0) {
      if (!activeTab || !configTypes.includes(activeTab)) {
        setActiveTab(configTypes[0]);
      }
    }
  }, [configTypes, activeTab]);

  // Load chats for non-super-admins
  useEffect(() => {
    if (!isPlatformOnlySuperAdmin() && !chatsLoaded && !chatsLoading) {
      loadChats();
    }
  }, [isPlatformOnlySuperAdmin, chatsLoaded, chatsLoading, loadChats]);

  // Reset editing when tab or configs change
  useEffect(() => {
    setIsEditing(false);
    setFormValues({});
    setOriginalValues({});
    setSaveSuccess(false);
    setSaveError(null);
    setJsonErrors({});
  }, [activeTab, selectedChatId]);

  /* ─── Role permissions (derived from role booleans, additive) ─── */
  const canEdit = (configType) => editableConfigTypesFor(effectiveRoles()).includes(configType);

  /* ─── Config type info ─── */
  const getConfigInfo = useCallback(
    (configType) => {
      const info = {
        vector_store: {
          title: translate("vectorStoreSettings"),
          description: translate("vectorStoreDescription"),
        },
        user_editable: {
          title: translate("userEditableSettings"),
          description: translate("userEditableDescription"),
        },
        workflow: {
          title: translate("workflowSettings"),
          description: translate("workflowSettingsDescription"),
        },
        prompts: {
          title: translate("promptTemplates"),
          description: translate("promptTemplatesDescription"),
        },
      };
      return (
        info[configType] || {
          title: configType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          description: translate("configurationSettings"),
        }
      );
    },
    [translate]
  );

  /* ─── Edit actions ─── */
  const startEditing = () => {
    if (!activeTab || !configs[activeTab]) return;
    /* From configs[activeTab], the COMPLETE config — never the filtered render list.
       handleSave sends every key of formValues back, and update_config replaces the
       whole settings JSONB, so a hidden field dropped here would be deleted from the
       row. Hiding is a render concern and must stay one. */
    const values = {};
    Object.entries(configs[activeTab]).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        values[key] = JSON.stringify(value, null, 2);
      } else {
        values[key] = value;
      }
    });
    setFormValues(values);
    setOriginalValues(values);
    setIsEditing(true);
    setSaveSuccess(false);
    setSaveError(null);
    setJsonErrors({});
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setFormValues({});
    setOriginalValues({});
    setJsonErrors({});
  };

  /* One box, two callers: read mode passes a badge, edit mode passes a Toggle.
     Groups come from the response envelope (never from `data`, which the page echoes
     back on save) and only when BOTH are populated — a lone header labels nothing. */
  const renderBoolMapBox = (key, current, controlFor) => {
    const groups = visibility?.toggles?.[key];
    /* `hidden` names toggles that would do nothing if flipped. They stay in `current`
       (and so in what the page saves) and are only left out of the rendering. `inert` is
       different: the whole dict is switched off, which is said in the caption and never
       by emptying the box. */
    const hidden = new Set(groups?.hidden || []);
    const entries = Object.entries(current).filter(([name]) => !hidden.has(name));
    const on = entries.filter(([, v]) => v === true).length;
    const inGroup = (names) => entries.filter(([name]) => (names || []).includes(name));
    const generic = groups ? inGroup(groups.generic) : [];
    const platform = groups ? inGroup(groups.platform) : [];
    /* Anything the report did not place keeps its own trailing group, so a tool added
       since this page loaded is never silently dropped from the list. */
    const placed = new Set([...(groups?.generic || []), ...(groups?.platform || [])]);
    const grouped = generic.length > 0 && platform.length > 0;
    const ungrouped = grouped ? entries.filter(([name]) => !placed.has(name)) : [];

    const row = ([name, enabled]) => (
      <div key={name} className="flex items-center justify-between gap-3 px-3 py-2">
        <label className="text-sm truncate" style={{ color: "var(--color-text-primary)" }} title={name}>
          {formatLabel(name)}
        </label>
        {controlFor(name, enabled)}
      </div>
    );

    const heading = (text) => (
      <div
        className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide"
        style={{ color: "var(--color-text-muted)", backgroundColor: "var(--color-surface)" }}
      >
        {text}
      </div>
    );

    return (
      <>
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: "1px solid var(--color-border)", backgroundColor: "var(--color-surface-raised)" }}
        >
          <div
            className="px-3 py-2 text-xs tabular-nums"
            style={{ color: "var(--color-text-muted)", borderBottom: "1px solid var(--color-border)" }}
          >
            {on} / {entries.length}
          </div>
          {grouped ? (
            <>
              {heading(translate("genericTools") || "Generic Tools")}
              <div className="divide-y divide-[var(--color-border)]">{generic.map(row)}</div>
              {heading(translate("platformTools") || "Platform Tools")}
              <div className="divide-y divide-[var(--color-border)]">{platform.map(row)}</div>
              {ungrouped.length > 0 && (
                <>
                  {heading(translate("otherTools"))}
                  <div className="divide-y divide-[var(--color-border)]">{ungrouped.map(row)}</div>
                </>
              )}
            </>
          ) : (
            <div className="divide-y divide-[var(--color-border)]">{entries.map(row)}</div>
          )}
        </div>
        {/* Outside the box, as a field caption: inside it read as part of the last row
            and made that row look taller than the others. Shown in read mode too — a list
            half the length of every other tenant's needs its explanation most where the
            reader cannot click Edit to find out why. */}
        {((groups?.hidden || []).length > 0 || groups?.inert) && HIDDEN_REASON_KEYS[groups.reason] && (
          <p className="text-xs mt-2" style={{ color: "var(--color-text-muted)" }}>
            {translate(HIDDEN_REASON_KEYS[groups.reason])}
          </p>
        )}
      </>
    );
  };

  /* A boolMap is edited through toggles but still travels as the JSON string that
     startEditing produced, so handleSave's JSON.parse path and the live JSON
     validation both keep working untouched. */
  const parseBoolMap = (raw, fallback) => {
    if (typeof raw !== "string") return raw ?? fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  };

  const boolMapFromForm = (key, fallback) => parseBoolMap(formValues[key], fallback);

  /* Through the functional updater, never through the render closure: two flips landing
     before a re-render would otherwise both start from the same snapshot and the second
     would discard the first. */
  const handleBoolMapToggle = (key, name, val, fallback) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: JSON.stringify({ ...parseBoolMap(prev[key], fallback), [name]: val }, null, 2),
    }));
  };

  const handleFieldChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    // Validate JSON fields live
    const originalValue = configs[activeTab]?.[key];
    if (typeof originalValue === "object" && originalValue !== null) {
      const result = validateJson(value);
      setJsonErrors((prev) => {
        const next = { ...prev };
        if (result.valid) {
          delete next[key];
        } else {
          next[key] = result.error;
        }
        return next;
      });
    }
  };

  /* Also the create action for a type with no stored row, where `formValues` is empty
     and the payload is therefore `{}`. That is deliberate and it is the whole payload
     the page can honestly send: the endpoint returns no defaults for a missing type, so
     there is nothing to echo back. The backend accepts it (there are no stored keys for
     a partial write to destroy), writes the row, and the next GET answers with the
     fields. Do not "fix" this by refusing an empty payload. */
  const handleSave = async () => {
    if (!activeTab || Object.keys(jsonErrors).length > 0) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const configToSave = {};
      Object.entries(formValues).forEach(([key, value]) => {
        const originalValue = configs[activeTab]?.[key];
        if (typeof originalValue === "object" && originalValue !== null) {
          try {
            configToSave[key] = JSON.parse(value);
          } catch {
            configToSave[key] = value;
          }
        } else {
          configToSave[key] = value;
        }
      });
      await onUpdate(activeTab, configToSave);
      setIsEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving config:", err);
      setSaveError(translate("failedToUpdateConfiguration") || "Failed to save configuration");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = useMemo(
    () => JSON.stringify(formValues) !== JSON.stringify(originalValues),
    [formValues, originalValues]
  );

  /* ─── Config import / export ─── */
  const handleExport = () => {
    if (!activeTab || !configs?.[activeTab]) return;
    const json = JSON.stringify(configs[activeTab], null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const chatName = selectedChat?.name ? `_${selectedChat.name.replace(/\s+/g, "_")}` : "";
    a.download = `${activeTab}${chatName}_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeTab) return;
    setImportError(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (typeof imported !== "object" || imported === null || Array.isArray(imported)) {
          setImportError(translate("invalidJsonFile") || "Invalid JSON file — expected an object with key-value pairs");
          return;
        }
        /* An older, complete export carries the prompt keys the endpoint no longer
           returns for this chat. Those are inactive, not unknown: they are dropped (the
           stored values stay) while anything nobody recognises is still refused. */
        const { payload, unknownKeys } = prepareConfigImport(
          imported,
          Object.keys(configs[activeTab] || {}),
          visibility?.hidden_keys?.[activeTab] || []
        );
        if (unknownKeys.length > 0) {
          setImportError(
            (translate("unknownConfigKeys") || "Unknown keys in imported file") +
            `: ${unknownKeys.join(", ")}`
          );
          return;
        }
        // Confirm before applying
        const confirmed = window.confirm(
          translate("confirmImportConfig") ||
          `Are you sure you want to import and overwrite the "${activeTab}" configuration?`
        );
        if (!confirmed) return;
        await onUpdate(activeTab, payload);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch {
        setImportError(translate("invalidJsonFile") || "Invalid JSON file — could not parse");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  /* ─── Render field value (read-only) ─── */
  const renderFieldValue = (key, value) => {
    const type = detectValueType(key, value);

    if (type === "boolean") {
      return (
        <span className={value === true ? "badge-active" : "badge-inactive"}>
          <span>{value === true ? "●" : "○"}</span>
          {value === true ? translate("enabled") || "Enabled" : translate("disabled") || "Disabled"}
        </span>
      );
    }

    if (type === "boolMap") {
      return renderBoolMapBox(key, value, (name, enabled) => (
        <span className={enabled === true ? "badge-active" : "badge-inactive"}>
          <span>{enabled === true ? "●" : "○"}</span>
          {enabled === true ? translate("enabled") || "Enabled" : translate("disabled") || "Disabled"}
        </span>
      ));
    }

    if (type === "json") {
      return (
        <pre
          className="text-xs leading-relaxed whitespace-pre-wrap break-all"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            color: "var(--color-text-secondary)",
            backgroundColor: "var(--color-surface-raised)",
            padding: "10px 12px",
            borderRadius: "8px",
            border: "1px solid var(--color-border)",
            maxHeight: "140px",
            overflowY: "auto",
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    if (type === "number") {
      return (
        <span
          className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold"
          style={{
            backgroundColor: "var(--color-surface-raised)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text-primary)",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {String(value)}
        </span>
      );
    }

    if (type === "largeText" || type === "mediumText") {
      return (
        <div
          className="text-sm leading-relaxed whitespace-pre-wrap"
          style={{
            color: "var(--color-text-secondary)",
            backgroundColor: "var(--color-surface-raised)",
            padding: "12px 14px",
            borderRadius: "8px",
            border: "1px solid var(--color-border)",
            maxHeight: type === "largeText" ? "180px" : "120px",
            overflowY: "auto",
            fontSize: "13px",
            lineHeight: "1.6",
          }}
        >
          {String(value)}
        </div>
      );
    }

    // Short text — styled as a read-only field
    const strValue = String(value);
    const isEmpty = !strValue || strValue === "undefined" || strValue === "null";
    return (
      <div
        className="flex items-center gap-2 px-3 rounded-lg text-sm"
        style={{
          backgroundColor: "var(--color-surface-raised)",
          border: "1px solid var(--color-border)",
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "13px",
          color: isEmpty ? "var(--color-text-muted)" : "var(--color-text-primary)",
          wordBreak: "break-all",
          minHeight: "38px",
        }}
      >
        {isEmpty ? "" : strValue}
      </div>
    );
  };

  /* ─── Render field input (edit mode) ─── */
  const renderFieldInput = (key, value) => {
    const currentValue = formValues[key];
    const type = detectValueType(key, value);

    if (type === "boolean") {
      return (
        <Toggle
          checked={currentValue === true || currentValue === "true"}
          onChange={(val) => handleFieldChange(key, val)}
        />
      );
    }

    if (type === "boolMap") {
      return renderBoolMapBox(key, boolMapFromForm(key, value), (name, enabled) => (
        <Toggle checked={enabled === true} onChange={(val) => handleBoolMapToggle(key, name, val, value)} />
      ));
    }

    if (type === "json") {
      const hasError = jsonErrors[key];
      return (
        <div>
          <textarea
            value={currentValue ?? ""}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            rows={8}
            className="field-input resize-y w-full"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "12px",
              lineHeight: "1.6",
              borderColor: hasError ? "var(--color-error)" : undefined,
            }}
            placeholder='{"key": "value"}'
          />
          {hasError && (
            <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: "var(--color-error)" }}>
              <AlertCircle size={12} />
              {translate("invalidJson") || "Invalid JSON"}: {hasError}
            </p>
          )}
        </div>
      );
    }

    if (type === "number") {
      return (
        <input
          type="number"
          step="any"
          value={currentValue ?? ""}
          onChange={(e) => handleFieldChange(key, e.target.value === "" ? "" : Number(e.target.value))}
          className="field-input"
          style={{ fontFamily: "'JetBrains Mono', monospace", maxWidth: "200px" }}
        />
      );
    }

    if (type === "largeText") {
      const charCount = (currentValue ?? "").length;
      return (
        <div>
          <textarea
            value={currentValue ?? ""}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            rows={10}
            className="field-input resize-y w-full"
            style={{ fontSize: "13px", lineHeight: "1.6" }}
          />
          <p className="text-xs mt-1 text-right" style={{ color: "var(--color-text-muted)" }}>
            {charCount} {translate("characters") || "characters"}
          </p>
        </div>
      );
    }

    if (type === "mediumText") {
      return (
        <textarea
          value={currentValue ?? ""}
          onChange={(e) => handleFieldChange(key, e.target.value)}
          rows={3}
          className="field-input resize-y w-full"
          style={{ fontSize: "13px" }}
        />
      );
    }

    return (
      <input
        type="text"
        value={currentValue ?? ""}
        onChange={(e) => handleFieldChange(key, e.target.value)}
        className="field-input w-full"
      />
    );
  };

  /* ─── Loading skeleton — wait for both configs AND chats ─── */
  const isChatsReady = isPlatformOnlySuperAdmin() || (chatsLoaded && !chatsLoading);
  if ((isLoading && !configs) || (!isPlatformOnlySuperAdmin() && !isChatsReady)) {
    return (
      <div className="space-y-6 page-enter animate-pulse">
        <div className="pb-5" style={{ borderBottom: "1px solid var(--color-border)" }}>
          <div className="h-7 w-64 rounded-lg mb-2 bg-[var(--color-skeleton)]" />
          <div className="h-4 w-96 rounded bg-[var(--color-skeleton-light)]" />
        </div>
        <div className="flex gap-6">
          <div className="w-56 flex-shrink-0 space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 rounded-xl bg-[var(--color-skeleton-light)]" />
            ))}
          </div>
          <div className="flex-1 space-y-6">
            <div className="h-16 rounded-xl bg-[var(--color-skeleton-light)]" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-32 rounded bg-[var(--color-skeleton)]" />
                <div className="h-10 rounded-lg bg-[var(--color-skeleton-light)]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const isActiveTabMissing = activeTab ? missingTypeSet.has(activeTab) : false;
  /* No fields, because the backend sends none for a type with no row and this page has
     no schema of its own to invent them from. Inventing one here would be a second copy
     of the backend's config classes, drifting from them silently, and for `prompts` it
     would put Radio Popular's default text in another tenant's row, the very thing
     RPAA-225 removed. What the tab carries in this state is the create action. */
  const activeConfig = isActiveTabMissing ? EMPTY_CONFIG : activeTab ? configs?.[activeTab] : null;
  const activeConfigInfo = activeTab ? getConfigInfo(activeTab) : null;
  const activeMeta = activeTab ? CONFIG_META[activeTab] : null;
  const ActiveIcon = activeMeta?.icon || Database;
  const isTabEditable = activeTab ? canEdit(activeTab) : false;
  /* The prompt keys nothing this chat reads never reach the page: the endpoint removes
     them from `data.prompts` and restores them from the stored row on save, so there is
     nothing to filter here. */
  /* The ingestion schedule pair is different: BOTH keys are always in `data`, and the
     mode says which one is read. Filtered from the render only — they stay in
     `formValues` and so in what handleSave sends, exactly as renderBoolMapBox's hidden
     toggles do, because update_config replaces the whole settings JSONB.
     Read from `formValues` while editing so that changing the mode reveals the field it
     applies to immediately: the response envelope is computed from the stored config and
     would need a save first, which would make setting fixed times two saves (RPAA-265). */
  const scheduleMode = isEditing
    ? formValues.product_ingestion_schedule_mode
    : activeConfig?.product_ingestion_schedule_mode;
  const hiddenScheduleKeys =
    activeTab === "workflow" ? inapplicableScheduleKeys(scheduleMode) : [];
  const configEntries = activeConfig
    ? Object.entries(activeConfig).filter(([key]) => !hiddenScheduleKeys.includes(key))
    : [];

  // Split fields: compact cards (boolean, number) vs full-width (text, json, etc.)
  const compactFields = configEntries.filter(([key, value]) => {
    const type = detectValueType(key, value);
    return type === "boolean" || type === "number";
  });
  const fullFields = configEntries.filter(([key, value]) => {
    const type = detectValueType(key, value);
    return type !== "boolean" && type !== "number";
  });

  return (
    <div className="space-y-6 page-enter">
      {/* Page header */}
      <div
        className="flex items-start justify-between pb-5"
        style={{ borderBottom: "1px solid var(--color-border)" }}
      >
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
            {translate("configurationManagement")}
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            {translate("configurationManagementDescription")}
          </p>
        </div>

        {/* Chat Selector */}
        {!isPlatformOnlySuperAdmin() && chats && chats.filter((chat) => chat.is_active).length > 1 && (
          <CustomSelect
            value={selectedChatId || ""}
            onChange={(val) => setSelectedChatId(val)}
            label={translate("selectChat") || "Select Chat"}
            icon={MessageSquare}
            disabled={chatsLoading || isLoading}
            className="w-56 flex-shrink-0"
            options={chats
              .filter((chat) => chat.is_active)
              .sort((a, b) => (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0))
              .map((chat) => ({ value: chat.id, label: chat.name }))
            }
          />
        )}
      </div>

      {/* Chat info banner */}
      {selectedChat && activeChatsCount > 1 && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{
            backgroundColor: "var(--color-mint-muted)",
            border: "1px solid var(--color-mint-border)",
          }}
        >
          <span className="font-medium" style={{ color: "var(--color-mint-dark)" }}>
            {selectedChat.name}
          </span>
          <span style={{ color: "var(--color-text-secondary)" }}>
            {" — "}
            {translate("configsForThisChat") || "Viewing configurations for this chat"}
          </span>
        </div>
      )}

      {/* No configs. A type this chat has no row for still counts as a tab, so the page
          is only empty when there is nothing at all to offer. */}
      {configTypes.length === 0 && !isLoading && (
        <div className="text-center py-16">
          <Database size={40} style={{ color: "var(--color-text-muted)", opacity: 0.4 }} className="mx-auto mb-4" />
          <p className="text-sm font-medium" style={{ color: "var(--color-text-muted)" }}>
            {translate("noConfigurationsAvailable")}
          </p>
          <p className="text-xs mt-2" style={{ color: "var(--color-text-muted)", opacity: 0.6 }}>
            {hasAllAccess()
              ? translate("noConfigurationsFound")
              : translate("regularUserAccessInfo")}
          </p>
        </div>
      )}

      {/* Main layout: Sidebar tabs + Content */}
      {configTypes.length > 0 && (
        <div className="flex gap-6" style={{ minHeight: "500px" }}>
          {/* ─── Left: Vertical tabs (hidden when only one config) ─── */}
          {configTypes.length > 1 && (
          <nav className="w-56 flex-shrink-0 space-y-1">
            {configTypes.map((configType) => {
              const meta = CONFIG_META[configType] || {};
              const Icon = meta.icon || Database;
              const info = getConfigInfo(configType);
              const isActive = activeTab === configType;
              const editable = canEdit(configType);

              return (
                <button
                  key={configType}
                  onClick={() => setActiveTab(configType)}
                  className="w-full text-left rounded-xl px-4 py-3 transition-all duration-150 group relative"
                  style={{
                    backgroundColor: isActive ? "var(--color-mint-muted)" : "transparent",
                    border: isActive ? "1px solid var(--color-mint-border)" : "1px solid transparent",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                      style={{
                        backgroundColor: isActive ? "var(--color-mint-muted)" : "var(--color-surface-raised)",
                      }}
                    >
                      <Icon
                        size={16}
                        style={{
                          color: isActive ? "var(--color-mint)" : "var(--color-text-muted)",
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium truncate"
                        style={{
                          color: isActive ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                        }}
                      >
                        {info.title}
                      </p>
                      {/* Same slot, same shape as the read-only caption: the tab of a
                          type with no stored row has to say so, or it reads as a tab
                          whose fields simply failed to load. */}
                      {missingTypeSet.has(configType) && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <AlertCircle size={10} style={{ color: "var(--color-amber-dark)" }} />
                          <span className="text-[10px]" style={{ color: "var(--color-amber-dark)" }}>
                            {translate("notConfigured")}
                          </span>
                        </div>
                      )}
                      {!editable && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Lock size={10} style={{ color: "var(--color-text-muted)" }} />
                          <span className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                            {translate("readOnly") || "Read only"}
                          </span>
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <ChevronRight
                        size={14}
                        style={{ color: "var(--color-mint)" }}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
          )}

          {/* ─── Right: Content area ─── */}
          <div className="flex-1 min-w-0 relative">
            {/* Loading overlay when switching chats */}
            {isLoading && configs && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl">
                <div className="absolute inset-0 rounded-xl" style={{ backgroundColor: "var(--color-bg)", opacity: 0.7 }} />
                <div className="relative flex items-center gap-3 px-5 py-3 rounded-xl" style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                  <Loader2 size={18} className="animate-spin" style={{ color: "var(--color-mint)" }} />
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("loadingConfigurations") || "Loading configurations..."}
                  </span>
                </div>
              </div>
            )}
            {activeConfig && (
              <div
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                }}
              >
                {/* Content header */}
                <div
                  className="px-6 py-5 flex items-center justify-between"
                  style={{ borderBottom: "1px solid var(--color-border)" }}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: activeMeta?.bgColor || "var(--color-mint-muted)" }}
                    >
                      <ActiveIcon size={20} style={{ color: activeMeta?.color || "var(--color-mint)" }} />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>
                        {activeConfigInfo?.title}
                      </h2>
                      <p className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>
                        {activeConfigInfo?.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Save success indicator */}
                    {saveSuccess && (
                      <span className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg"
                        style={{ color: "var(--color-success)", backgroundColor: "rgba(34,197,94,0.08)" }}
                      >
                        <CheckCircle size={14} />
                        {translate("saved") || "Saved"}
                      </span>
                    )}

                    {/* Import/Export. Both are out while the row does not exist: there is
                        nothing to export, and the import refuses every key of the file
                        because it validates them against the keys the page holds, which
                        are none. Create the row first and they come back with the
                        fields, which is also the order the rollout rule wants, the
                        target's own export being the thing to diff against. */}
                    {isTabEditable && !isEditing && !isActiveTabMissing && (
                      <>
                        <button onClick={handleExport} className="btn-secondary" style={{ padding: "8px 14px" }}>
                          <Download size={14} />
                          <span>{translate("export") || "Export"}</span>
                        </button>
                        <button onClick={() => fileInputRef.current?.click()} className="btn-secondary" style={{ padding: "8px 14px" }}>
                          <Upload size={14} />
                          <span>{translate("import") || "Import"}</span>
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".json"
                          onChange={handleImport}
                          className="hidden"
                        />
                      </>
                    )}

                    {isTabEditable && !isEditing && !isActiveTabMissing && (
                      <button onClick={startEditing} className="btn-primary" style={{ padding: "8px 16px" }}>
                        <Sliders size={14} />
                        <span>{translate("editSettings") || "Edit Settings"}</span>
                      </button>
                    )}

                    {/* One action instead of the Edit/Save pair, because there is nothing
                        to edit yet. Reusing that pair here would be a dead end: Save is
                        disabled while `hasChanges` is false, and with no fields it can
                        never become true. This button is enabled from the start, since
                        creating the row IS the change, and it goes through the same
                        `handleSave` and so the same PUT that every other save uses. */}
                    {isTabEditable && isActiveTabMissing && (
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="btn-primary"
                        style={{ padding: "8px 16px" }}
                      >
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        <span>{isSaving ? translate("creatingConfiguration") : translate("createConfiguration")}</span>
                      </button>
                    )}

                    {isEditing && (
                      <>
                        <button onClick={cancelEditing} className="btn-secondary">
                          <X size={14} />
                          <span>{translate("cancel") || "Cancel"}</span>
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={isSaving || Object.keys(jsonErrors).length > 0 || !hasChanges}
                          className="btn-primary"
                        >
                          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                          <span>{isSaving ? translate("saving") || "Saving..." : translate("saveChanges") || "Save Changes"}</span>
                        </button>
                      </>
                    )}

                    {!isTabEditable && (
                      <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                        style={{
                          color: "var(--color-text-muted)",
                          backgroundColor: "var(--color-surface-raised)",
                          border: "1px solid var(--color-border)",
                        }}
                      >
                        <Lock size={12} />
                        {translate("readOnly") || "Read Only"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Save error */}
                {saveError && (
                  <div
                    className="mx-6 mt-4 px-4 py-3 rounded-lg flex items-center gap-2 text-sm"
                    style={{ backgroundColor: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", color: "var(--color-error)" }}
                  >
                    <AlertCircle size={14} />
                    {saveError}
                  </div>
                )}

                {/* Import error */}
                {importError && (
                  <div
                    className="mx-6 mt-4 px-4 py-3 rounded-lg flex items-center justify-between gap-2 text-sm"
                    style={{ backgroundColor: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", color: "var(--color-error)" }}
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle size={14} />
                      {importError}
                    </div>
                    <button onClick={() => setImportError(null)} className="p-1 rounded hover:bg-red-100 transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                )}

                {/* Fields */}
                <div className="px-6 py-5 space-y-6">
                  {isActiveTabMissing && (
                    <div
                      className="rounded-xl px-4 py-4"
                      style={{
                        backgroundColor: "var(--color-amber-light)",
                        border: "1px solid var(--color-amber)",
                        color: "var(--color-amber-text)",
                      }}
                    >
                      <p className="text-sm font-medium flex items-center gap-2">
                        <AlertCircle size={14} />
                        {translate("notConfiguredTitle")}
                      </p>
                      <p className="text-xs mt-1.5 leading-relaxed">
                        {translate("notConfiguredDescription")}
                      </p>
                    </div>
                  )}

                  {/* Above BOTH grids, not inside one: the two keys this can hide
                      are a number and a text field, so they land in different
                      sections and a caption placed next to either would be absent
                      exactly half the time. */}
                  {hiddenScheduleKeys.map(
                    (key) =>
                      INAPPLICABLE_SCHEDULE_REASON_KEYS[key] && (
                        <p key={key} className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                          {translate(INAPPLICABLE_SCHEDULE_REASON_KEYS[key])}
                        </p>
                      )
                  )}

                  {/* Compact fields (booleans + numbers) in a responsive grid */}
                  {compactFields.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {compactFields.map(([key, value]) => {
                        const type = detectValueType(key, value);
                        const isBoolean = type === "boolean";
                        const isNumber = type === "number";
                        const isText = type === "text";

                        return (
                          <div
                            key={key}
                            className={`rounded-xl px-4 py-3 ${isText ? "" : "flex items-center justify-between"}`}
                            style={{
                              backgroundColor: "var(--color-surface-raised)",
                              border: "1px solid var(--color-border)",
                            }}
                          >
                            <label
                              className={`text-xs font-medium uppercase tracking-wide ${isText ? "block mb-1.5" : "truncate mr-3 text-sm normal-case"}`}
                              style={{ color: isText ? "var(--color-text-muted)" : "var(--color-text-primary)" }}
                              title={formatLabel(key)}
                            >
                              {formatLabel(key)}
                            </label>

                            {isEditing ? (
                              isBoolean ? (
                                <Toggle
                                  checked={formValues[key] === true || formValues[key] === "true"}
                                  onChange={(val) => handleFieldChange(key, val)}
                                />
                              ) : isNumber ? (
                                <input
                                  type="number"
                                  step="any"
                                  value={formValues[key] ?? ""}
                                  onChange={(e) => handleFieldChange(key, e.target.value === "" ? "" : Number(e.target.value))}
                                  className="field-input text-right"
                                  style={{ width: "90px", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", padding: "4px 10px" }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={formValues[key] ?? ""}
                                  onChange={(e) => handleFieldChange(key, e.target.value)}
                                  className="field-input w-full"
                                  style={{ fontSize: "13px" }}
                                />
                              )
                            ) : isText ? (
                              <p
                                className="text-sm truncate"
                                style={{ color: "var(--color-text-primary)", fontFamily: "'JetBrains Mono', monospace", fontSize: "12px" }}
                                title={String(value)}
                              >
                                {String(value)}
                              </p>
                            ) : (
                              renderFieldValue(key, value)
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Full-width fields (text, json, large text) */}
                  {fullFields.length > 0 && (
                    <div className="space-y-4">
                      {fullFields.map(([key, value]) => {
                        return (
                          <div key={key}>
                            <label
                              className="block text-xs font-medium uppercase tracking-wide mb-2"
                              style={{ color: "var(--color-text-primary)" }}
                            >
                              {formatLabel(key)}
                            </label>
                            {isEditing
                              ? renderFieldInput(key, value)
                              : renderFieldValue(key, value)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Floating save bar when editing with changes */}
                {isEditing && hasChanges && (
                  <div
                    className="sticky bottom-0 px-6 py-4 flex items-center justify-between"
                    style={{
                      backgroundColor: "var(--color-surface)",
                      borderTop: "1px solid var(--color-border)",
                      boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
                    }}
                  >
                    <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      {translate("unsavedChanges") || "You have unsaved changes"}
                    </p>
                    <div className="flex items-center gap-2">
                      <button onClick={cancelEditing} className="btn-secondary" style={{ padding: "6px 14px" }}>
                        {translate("discard") || "Discard"}
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving || Object.keys(jsonErrors).length > 0}
                        className="btn-primary"
                        style={{ padding: "6px 14px" }}
                      >
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        <span>{isSaving ? translate("saving") || "Saving..." : translate("saveChanges") || "Save Changes"}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurationsPage;
